import { motion } from "framer-motion";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/**
 * Plain answers to what people actually ask before booking a band.
 *
 * These mirror the FAQPage structured data in public/index.html word for word —
 * markup that claims answers the page doesn't show is a search guidelines
 * violation, and answer engines cross-check. Every answer is rendered into the
 * DOM rather than hidden behind a toggle, so a crawler that doesn't run
 * JavaScript still reads all of it.
 */
const FAQS = [
  {
    q: "What kind of music does the Randy Loveless Band play?",
    a: "Classic rock covers spanning the 1960s through the 2000s — the songs most people already know — alongside original material from Randy Loveless's album 'Trapped in a Montana Winter'.",
  },
  {
    q: "Where is the Randy Loveless Band based?",
    a: "The band is based in Kalispell, Montana, and performs at venues throughout Montana and the surrounding region, including bars, festivals and private events.",
  },
  {
    q: "How do you book the Randy Loveless Band?",
    a: "Use the booking form below with your date, venue and city. Inquiries go directly to the band, who reply by email.",
  },
  {
    q: "When is the Randy Loveless Band playing next?",
    a: "Upcoming dates are listed in the Shows section above and are kept up to date by the band. Joining the fan list means hearing about new dates first.",
  },
  {
    q: "Who has the Randy Loveless Band opened for?",
    a: "Randy Loveless has opened for Rick Derringer, Great White, Badfinger and ZZ Top.",
  },
];

export const Faq = () => {
  const settings = useSiteSettings();
  const location = settings.location || "Kalispell, Montana";

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-[#0F0808] py-24 md:py-32 px-6 md:px-16 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#DC2626]" />
            <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
              Before You Book
            </span>
          </div>
          <h2 className="font-anton uppercase text-white text-5xl md:text-6xl leading-[0.9]">
            Common
            <br />
            <span className="text-[#B91C1C]">Questions</span>
          </h2>
          <p className="text-white/60 font-manrope mt-6 max-w-sm">
            Anything else, just ask — the booking form is right below and goes
            straight to the band in {location}.
          </p>
        </motion.div>

        <div className="lg:col-span-7">
          <dl className="border-t border-white/10">
            {FAQS.map((item, i) => (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 5) * 0.06 }}
                data-testid={`faq-item-${i}`}
                className="border-b border-white/10 py-6"
              >
                <dt className="font-oswald uppercase tracking-wide text-white text-lg md:text-xl">
                  {item.q}
                </dt>
                <dd className="text-white/70 font-manrope mt-2 leading-relaxed">{item.a}</dd>
              </motion.div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
};
